/**
 * One room or direct message: header, the thread, the composer and the room
 * panel on the right (members, adding people, invites by email, settings).
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { IconCheck, IconClip, IconCopy, IconDoc, IconMore, IconQuote, IconReact, IconRobot } from "../components/icons";
import { DecisionList, SaveDecisionModal } from "../components/Decisions";
import { PersonTrigger } from "../components/ProfileCard";
import { byUrgency, NewTaskModal, TaskCard, TaskRow, useTaskList } from "../components/Tasks";
import { Avatar, ErrorNote, RoomDot } from "../components/ui";
import { RoomPromptCard } from "../components/WorkDrawer";
import { openDrawer, sessionOfRoom, useWork } from "../lib/work";
import { api, ApiError } from "../lib/api";
import { ACCEPT, removeUpload, sizeLabel, uploadFile, type FileView } from "../lib/files";
import { dayLabel, firstName, sameDay, segments, timeOf } from "../lib/format";
import {
  addMembers,
  deleteMessage,
  editMessage,
  joinRoom,
  loadBootstrap,
  loadMessages,
  markRead,
  quoteInto,
  REACTIONS,
  removeMember,
  setRoomHost,
  updateDraft,
  decideAction,
  toggleReaction,
  roomTitle,
  sendInvite,
  sendMessage,
  setActiveRoom,
  updateRoomSettings,
  EMPTY,
  useStore,
  type Message,
  type Person,
  type Room,
} from "../lib/store";

/** The @agents choice in the mention list: every agent of the room answers, one after the other. */
const EVERY_AGENT: Person = { id: "@agents", name: "agents", kind: "agent", avatarUrl: null, role: "member", email: null, lastSeenAt: null, online: true, agent: null };

const GROUP_MS = 5 * 60_000;

export function RoomView() {
  const { roomId = "" } = useParams();
  const room = useStore((s) => s.boot?.rooms.find((r) => r.id === roomId));
  const title = useStore((s) => (room ? roomTitle(s, room) : ""));
  const [panel, setPanel] = useState(() => matchMedia("(min-width: 1100px)").matches);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setActiveRoom(roomId);
    setError(null);
    loadMessages(roomId)
      .then(() => markRead(roomId))
      .catch((err) => setError(err instanceof ApiError ? err.message : "This room could not be loaded."));
    const onVisible = () => document.visibilityState === "visible" && void markRead(roomId);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      setActiveRoom(null);
    };
  }, [roomId]);

  if (!room) {
    return (
      <main className="welcome">
        <h1>This room is not available</h1>
        <p>It may be private, or it was removed.</p>
      </main>
    );
  }

  return (
    <div className={`room${panel ? " with-panel" : ""}`}>
      <section className="thread" aria-label={title}>
        <RoomHeader room={room} title={title} onTogglePanel={() => setPanel((p) => !p)} panelOpen={panel} />
        <ErrorNote error={error} />
        <MessageList room={room} />
        <AgentStatus room={room} />
        <Composer room={room} />
      </section>
      {panel && <RoomPanel room={room} onClose={() => setPanel(false)} />}
    </div>
  );
}

function RoomHeader({ room, title, onTogglePanel, panelOpen }: { room: Room; title: string; onTogglePanel: () => void; panelOpen: boolean }) {
  const person = useStore((s) => (room.dmWith ? s.boot?.people.find((p) => p.id === room.dmWith) : undefined));
  const project = useStore((s) => (room.projectId ? s.boot?.projects.find((p) => p.id === room.projectId) : undefined));
  return (
    <header className="thread-head">
      <div className="thread-title">
        {room.kind === "dm" && person ? <Avatar person={person} size={26} showOnline /> : <RoomDot color={room.color} />}
        <div>
          <h1>{room.kind === "dm" ? title : `#${title}`}</h1>
          <p>{room.kind === "dm" ? (person?.online ? "Online now" : "Direct message") : room.purpose || "No purpose yet"}</p>
        </div>
      </div>
      {project && (
        <Link className="chip project-chip" to={`/p/${project.id}`} title={`This room works on ${project.repo}`}>
          {project.name}
        </Link>
      )}
      {room.kind === "room" && <WorkButton room={room} />}
      {room.kind === "room" && (
        <button className="btn ghost small" type="button" onClick={onTogglePanel} aria-pressed={panelOpen}>
          {room.members.length} {room.members.length === 1 ? "member" : "members"}
        </button>
      )}
    </header>
  );
}

/** Admins open Claude's Work view for the room's session (its project's, or the room's own). */
function WorkButton({ room }: { room: Room }) {
  const isAdmin = useStore((s) => s.boot?.workspace.role === "admin");
  const claudeHere = useStore((s) => room.members.some((m) => s.boot?.people.find((p) => p.id === m.userId && p.agent?.kind === "claude_channel")));
  const known = useWork((w) => w.roomSession[room.id]);
  const session = useWork((w) => (known ? w.sessions[known] : undefined));
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    if (isAdmin && claudeHere && known === undefined) void sessionOfRoom(room.id).catch(() => undefined);
  }, [isAdmin, claudeHere, known, room.id]);
  if (!isAdmin || !claudeHere) return null;
  const open = async () => {
    const id = known ?? (await sessionOfRoom(room.id).catch(() => null));
    if (id) openDrawer(id);
    else {
      setNote("Claude has not worked in this room yet. Mention @Claude to start.");
      setTimeout(() => setNote(null), 4000);
    }
  };
  return (
    <span className="work-btn-wrap">
      <button className={`btn ghost small${session?.openPrompts ? " attention" : ""}`} type="button" onClick={() => void open()} title="Everything Claude does here: tools, files, plan, checklist, usage">
        Work view{session?.openPrompts ? ` (${session.openPrompts} waiting)` : session?.busy ? " (working)" : ""}
      </button>
      {note && <span className="work-btn-note">{note}</span>}
    </span>
  );
}

function MessageList({ room }: { room: Room }) {
  const list = useStore((s) => s.messages[room.id]);
  const people = useStore((s) => s.boot?.people ?? EMPTY);
  const me = useStore((s) => s.boot?.me.id);
  const isAdmin = useStore((s) => s.boot?.workspace.role === "admin");
  const projectName = useStore((s) => (room.projectId ? (s.boot?.projects.find((p) => p.id === room.projectId)?.name ?? null) : null));
  const ref = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const prevHeight = useRef(0);
  const prevFirst = useRef<number | undefined>(undefined);
  const items = list?.items ?? [];

  // Stay pinned to the bottom for new messages; keep the place when older ones load above.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const first = items[0]?.id;
    if (prevFirst.current !== undefined && first !== prevFirst.current && prevHeight.current) {
      el.scrollTop += el.scrollHeight - prevHeight.current;
    } else if (stick.current) {
      el.scrollTop = el.scrollHeight;
    }
    prevFirst.current = first;
    prevHeight.current = el.scrollHeight;
  }, [items]);

  useEffect(() => {
    stick.current = true;
    prevFirst.current = undefined;
  }, [room.id]);

  function onScroll() {
    const el = ref.current!;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    prevHeight.current = el.scrollHeight;
    if (el.scrollTop < 120 && list?.hasMore && !list.loading) void loadMessages(room.id, { older: true });
  }

  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const mentionable = useMemo(() => people.filter((p) => room.members.some((m) => m.userId === p.id)), [people, room.members]);

  return (
    <div className="messages" ref={ref} onScroll={onScroll} role="log" aria-live="polite">
      {list?.hasMore && <div className="older">{list.loading ? "Loading older messages" : ""}</div>}
      {list && !list.loading && items.length === 0 && (
        <div className="first-note">
          <b>This is the start of {room.kind === "dm" ? "your conversation" : `#${room.name}`}.</b>
          <span>{room.kind === "dm" ? "Messages here are only between the two of you." : room.purpose || "Say hello to the room."}</span>
        </div>
      )}
      {items.map((m, i) => {
        const prev = items[i - 1];
        const newDay = !prev || !sameDay(prev.createdAt, m.createdAt);
        const grouped = !newDay && prev && prev.authorId === m.authorId && !prev.deleted && new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_MS;
        return (
          <Fragment key={m.id}>
            {newDay && (
              <div className="day">
                <span>{dayLabel(m.createdAt)}</span>
              </div>
            )}
            <MessageRow
              message={m}
              author={byId.get(m.authorId ?? "")}
              grouped={Boolean(grouped)}
              mine={m.authorId === me}
              canDelete={m.authorId === me || isAdmin}
              canReact={room.isMember}
              canDecide={room.kind === "room" && room.isMember}
              projectName={projectName}
              people={mentionable}
              byId={byId}
              meId={me}
              isAdmin={isAdmin}
            />
          </Fragment>
        );
      })}
    </div>
  );
}

function MessageRow(p: {
  message: Message;
  author?: Person;
  grouped: boolean;
  mine: boolean;
  canDelete: boolean;
  canReact: boolean;
  canDecide: boolean;
  projectName: string | null;
  people: Person[];
  byId: Map<string, Person>;
  meId?: string;
  isAdmin?: boolean;
}) {
  const { message: m, author } = p;
  const [editing, setEditing] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [tasking, setTasking] = useState(false);
  const [draft, setDraft] = useState(m.body);
  const [error, setError] = useState<string | null>(null);
  const agent = author?.kind === "agent";
  const mentionsMe = Boolean(p.meId && m.mentions.includes(p.meId));

  async function save() {
    if (!draft.trim() || draft === m.body) return setEditing(false);
    try {
      if (m.draft) await updateDraft(m.id, { body: draft });
      else await editMessage(m.id, draft);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
    }
  }

  return (
    <article className={`msg${p.grouped ? " grouped" : ""}${agent ? " agent" : ""}${mentionsMe ? " me-mentioned" : ""}${m.draft ? ` draft-${m.draft.status}` : ""}`}>
      <div className="msg-side">
        {!p.grouped && author ? (
          <PersonTrigger person={author}>
            <Avatar person={author} size={34} />
          </PersonTrigger>
        ) : (
          <time className="hover-time">{timeOf(m.createdAt)}</time>
        )}
      </div>
      <div className="msg-main">
        {!p.grouped && (
          <header>
            {author ? (
              <PersonTrigger person={author} className="name-trigger">
                <b>{author.name}</b>
                {agent && <IconRobot />}
              </PersonTrigger>
            ) : (
              <b>Former member</b>
            )}
            {agent && <span className="agent-badge">{author?.agent?.model ? `agent · ${author.agent.model}` : "agent"}</span>}
            <time dateTime={m.createdAt}>{timeOf(m.createdAt)}</time>
          </header>
        )}
        {m.deleted ? (
          <p className="deleted">Message deleted</p>
        ) : editing ? (
          <div className="edit">
            <ErrorNote error={error} />
            <textarea
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) (e.preventDefault(), void save());
                if (e.key === "Escape") (setDraft(m.body), setEditing(false));
              }}
            />
            <div className="edit-actions">
              <button className="btn ghost small" type="button" onClick={() => (setDraft(m.body), setEditing(false))}>
                Cancel
              </button>
              <button className="btn primary small" type="button" onClick={() => void save()}>
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="body">
            {m.prompt ? (
              <RoomPromptCard prompt={m.prompt} />
            ) : m.action ? (
              <ActionCard action={m.action} isAdmin={Boolean(p.isAdmin)} meId={p.meId} byId={p.byId} />
            ) : m.taskId ? (
              <TaskCard message={m} roomId={m.roomId} />
            ) : (
              m.body && <MessageText body={m.body} people={p.people} meId={p.meId} />
            )}
            {m.files && m.files.length > 0 && <MessageFiles files={m.files} />}
            {m.editedAt && <span className="edited">(edited)</span>}
            {m.decision && (
              <span className="decision-tag" title="Saved as a decision: agents read it">
                Decision
              </span>
            )}
            {m.draft && <DraftBar message={m} isAdmin={Boolean(p.isAdmin)} byId={p.byId} onEdit={() => (setDraft(m.body), setEditing(true))} />}
          </div>
        )}
        {!m.deleted && m.reactions.length > 0 && (
          <div className="reactions">
            {m.reactions.map((r) => {
              const mine = Boolean(p.meId && r.userIds.includes(p.meId));
              const names = r.userIds.map((id) => p.byId.get(id)?.name ?? "Former member").join(", ");
              return (
                <button
                  key={r.emoji}
                  type="button"
                  className={`reaction${mine ? " mine" : ""}`}
                  title={names}
                  aria-label={`${r.emoji} ${r.userIds.length}, from ${names}${mine ? ". Click to remove yours" : ""}`}
                  disabled={!p.canReact}
                  onClick={() => void toggleReaction(m.id, r.emoji)}
                >
                  <span>{r.emoji}</span>
                  <b>{r.userIds.length}</b>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {!m.deleted && !editing && (
        <MessageTools
          message={m}
          authorName={author?.name ?? "Former member"}
          canReact={p.canReact}
          onEdit={p.mine ? () => (setDraft(m.body), setEditing(true)) : undefined}
          onDelete={p.canDelete ? () => confirm("Delete this message for everyone?") && void deleteMessage(m.id) : undefined}
          onDecide={p.canDecide && !m.decision && !m.taskId ? () => setDeciding(true) : undefined}
          onTask={p.canDecide && !m.taskId ? () => setTasking(true) : undefined}
        />
      )}
      {deciding && <SaveDecisionModal message={m} projectName={p.projectName} onClose={() => setDeciding(false)} />}
      {tasking && <NewTaskModal roomId={m.roomId} from={m} onClose={() => setTasking(false)} />}
    </article>
  );
}

/** What an agent proposes: nothing happens until an admin confirms it. */
function ActionCard({ action: a, isAdmin, meId, byId }: { action: NonNullable<Message["action"]>; isAdmin: boolean; meId?: string; byId: Map<string, Person> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const who = a.decidedBy ? (byId.get(a.decidedBy)?.name ?? "an admin") : "an admin";
  const decide = async (what: "confirm" | "cancel") => {
    setBusy(true);
    setError(null);
    try {
      const r = await decideAction(a.id, what);
      if (r.error) setError(r.error);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`action-card ${a.status}`}>
      <div className="action-head">
        <span className="action-label">Proposes</span>
        <b>{a.summary}</b>
      </div>
      {a.details.length > 0 && (
        <ul className="action-details">
          {a.details.map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      )}
      <div className="action-foot">
        {a.status === "proposed" && <span className="draft-chip">Waits for an admin</span>}
        {a.status === "running" && <span className="draft-chip">Running</span>}
        {a.status === "done" && <span className="draft-chip ok">Done, confirmed by {who}</span>}
        {a.status === "cancelled" && <span className="draft-chip off">Cancelled by {who}</span>}
        {a.status === "failed" && <span className="draft-chip bad">Did not work</span>}
        {a.status === "proposed" && isAdmin && (
          <button className="btn primary small" type="button" disabled={busy} onClick={() => void decide("confirm")}>
            Confirm
          </button>
        )}
        {a.status === "proposed" && (isAdmin || a.requestedBy === meId) && (
          <button className="btn ghost small" type="button" disabled={busy} onClick={() => void decide("cancel")}>
            Cancel
          </button>
        )}
        {(a.status === "done" || a.status === "failed") && a.result && <small className="muted-text">{a.result}</small>}
        {a.status === "done" && a.link && (
          <Link className="linkish" to={a.link}>
            Open
          </Link>
        )}
        {error && <small className="flow-error">{error}</small>}
      </div>
    </div>
  );
}

/** An event agent's draft: it waits for an admin, who approves, edits or discards it. */
function DraftBar({ message: m, isAdmin, byId, onEdit }: { message: Message; isAdmin: boolean; byId: Map<string, Person>; onEdit: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const d = m.draft!;
  const who = d.by ? (byId.get(d.by)?.name ?? "an admin") : "an admin";
  const act = async (status: "approved" | "discarded" | "draft") => {
    setBusy(true);
    setError(null);
    try {
      await updateDraft(m.id, { status });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="draft-bar">
      {d.status === "draft" && <span className="draft-chip">Draft, waits for approval</span>}
      {d.status === "approved" && <span className="draft-chip ok">Approved by {who}</span>}
      {d.status === "discarded" && <span className="draft-chip off">Discarded by {who}</span>}
      {isAdmin && d.status === "draft" && (
        <>
          <button className="btn primary small" type="button" disabled={busy} onClick={() => void act("approved")}>
            Approve
          </button>
          <button className="btn ghost small" type="button" disabled={busy} onClick={onEdit}>
            Edit
          </button>
          <button className="btn ghost small" type="button" disabled={busy} onClick={() => void act("discarded")}>
            Discard
          </button>
        </>
      )}
      {d.status === "approved" && (
        <button
          className="btn ghost small"
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(m.body).catch(() => undefined);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? "Copied" : "Copy text"}
        </button>
      )}
      {isAdmin && d.status === "discarded" && (
        <button className="linkish" type="button" disabled={busy} onClick={() => void act("draft")}>
          Bring it back
        </button>
      )}
      {error && <small className="flow-error">{error}</small>}
    </div>
  );
}

function MessageTools({
  message: m,
  authorName,
  canReact,
  onEdit,
  onDelete,
  onDecide,
  onTask,
}: {
  message: Message;
  authorName: string;
  canReact: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onDecide?: () => void;
  onTask?: () => void;
}) {
  const [open, setOpen] = useState<null | "react" | "more">(null);
  const [below, setBelow] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const quote = () => {
    const lines = m.body.split("\n").filter((l) => !l.startsWith("> "));
    quoteInto(m.roomId, `> ${authorName}: ${lines.join("\n> ")}\n\n`);
  };
  return (
    <div className={`msg-tools${open ? " open" : ""}`} role="toolbar" aria-label="Message actions" ref={ref}>
      {canReact && (
        <button
          type="button"
          title="Add reaction"
          aria-label="Add reaction"
          aria-expanded={open === "react"}
          onClick={() => {
            // Open below the message when there is no room above it in the thread.
            const box = ref.current?.getBoundingClientRect();
            const list = ref.current?.closest(".messages")?.getBoundingClientRect();
            setBelow(Boolean(box && list && box.top - 190 < list.top));
            setOpen(open === "react" ? null : "react");
          }}
        >
          <IconReact />
        </button>
      )}
      <button
        type="button"
        title={copied ? "Copied" : "Copy"}
        aria-label={copied ? "Copied" : "Copy the message"}
        onClick={async () => {
          await navigator.clipboard.writeText(m.body).catch(() => undefined);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? <IconCheck /> : <IconCopy />}
      </button>
      {canReact && (
        <button type="button" title="Quote" aria-label="Quote in your reply" onClick={quote}>
          <IconQuote />
        </button>
      )}
      {(onEdit || onDelete || onDecide || onTask) && (
        <button type="button" title="More" aria-label="More actions" aria-expanded={open === "more"} onClick={() => setOpen(open === "more" ? null : "more")}>
          <IconMore />
        </button>
      )}
      {open === "react" && (
        <div className={`reaction-picker${below ? " below" : ""}`} role="menu" aria-label="Pick a reaction">
          {REACTIONS.map((e) => (
            <button key={e} type="button" role="menuitem" onClick={() => (setOpen(null), void toggleReaction(m.id, e))}>
              {e}
            </button>
          ))}
        </div>
      )}
      {open === "more" && (
        <div className="more-menu" role="menu">
          {onTask && (
            <button type="button" role="menuitem" onClick={() => (setOpen(null), onTask())}>
              Create task
            </button>
          )}
          {onDecide && (
            <button type="button" role="menuitem" onClick={() => (setOpen(null), onDecide())}>
              Save as decision
            </button>
          )}
          {onEdit && (
            <button type="button" role="menuitem" onClick={() => (setOpen(null), onEdit())}>
              Edit
            </button>
          )}
          {onDelete && (
            <button type="button" role="menuitem" className="danger" onClick={() => (setOpen(null), onDelete())}>
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Pictures as previews that open full size; documents as cards that open in a new tab. */
function MessageFiles({ files }: { files: FileView[] }) {
  const [zoom, setZoom] = useState<string | null>(null);
  const images = files.filter((f) => f.kind === "image");
  const docs = files.filter((f) => f.kind !== "image");
  return (
    <div className="message-files">
      {images.length > 0 && (
        <div className={`file-images n${Math.min(images.length, 3)}`}>
          {images.map((f) => (
            <button key={f.id} type="button" className="file-image" onClick={() => setZoom(f.id)} title={f.name}>
              <img src={`/api/files/${f.id}`} alt={f.name} loading="lazy" width={f.width ?? undefined} height={f.height ?? undefined} />
            </button>
          ))}
        </div>
      )}
      {docs.map((f) => (
        <a key={f.id} className="file-card" href={`/api/files/${f.id}`} target="_blank" rel="noreferrer">
          <IconDoc />
          <span>
            {f.name}
            <small>
              {f.kind === "pdf" ? "PDF" : "Text"} · {sizeLabel(f.size)}
            </small>
          </span>
        </a>
      ))}
      {zoom && (
        <button type="button" className="help-zoom" onClick={() => setZoom(null)} aria-label="Close the picture">
          <img src={`/api/files/${zoom}`} alt="" />
        </button>
      )}
    </div>
  );
}

/** Lines starting with "> " become a quote block; the rest is text with links and mentions. */
function MessageText({ body, people, meId }: { body: string; people: Person[]; meId?: string }) {
  const blocks = useMemo(() => {
    const out: { quote: boolean; text: string }[] = [];
    for (const line of body.split("\n")) {
      const quote = line.startsWith("> ") || line === ">";
      const text = quote ? line.replace(/^> ?/, "") : line;
      const last = out.at(-1);
      if (last && last.quote === quote) last.text += `\n${text}`;
      else out.push({ quote, text });
    }
    return out;
  }, [body]);
  return (
    <>
      {blocks.map((b, i) =>
        b.quote ? (
          <blockquote key={i}>
            <Inline body={b.text} people={people} meId={meId} />
          </blockquote>
        ) : (
          <p key={i}>
            <Inline body={b.text.replace(/^\n+|\n+$/g, "")} people={people} meId={meId} />
          </p>
        ),
      )}
    </>
  );
}

function Inline({ body, people, meId }: { body: string; people: Person[]; meId?: string }) {
  const parts = useMemo(() => segments(body, people), [body, people]);
  return (
    <>
      {parts.map((s, i) =>
        s.kind === "link" ? (
          <a key={i} href={s.text} target="_blank" rel="noopener noreferrer">
            {s.text}
          </a>
        ) : s.kind === "mention" ? (
          <span key={i} className={`mention${s.userId === meId ? " me" : ""}`}>
            {s.text}
          </span>
        ) : (
          <Fragment key={i}>{s.text}</Fragment>
        ),
      )}
    </>
  );
}

/** While Claude works on a request from this room, the owner can stop it, like Stop in Claude Code. */
function StopClaude({ room }: { room: Room }) {
  const owner = useStore((s) => Boolean(s.boot?.me.isOwner));
  const claudeId = useStore((s) => s.boot?.people.find((p) => p.agent?.kind === "claude_channel")?.id);
  const working = useStore((s) => Boolean(claudeId && s.thinking[room.id]?.includes(claudeId)));
  const [busy, setBusy] = useState(false);
  if (!owner || !working) return null;
  return (
    <button
      className="btn danger small stop-claude"
      type="button"
      disabled={busy}
      title="Stop what Claude is doing now. It keeps everything done so far."
      onClick={async () => {
        setBusy(true);
        await api(`/api/claude/rooms/${room.id}/interrupt`, {}, "POST").catch(() => undefined);
        setBusy(false);
      }}
    >
      Stop
    </button>
  );
}

/** "Claude is thinking", "Claude is offline" and the server's short notices, above the composer. */
function AgentStatus({ room }: { room: Room }) {
  const people = useStore((s) => s.boot?.people ?? EMPTY);
  const thinking = useStore((s) => s.thinking[room.id]);
  const notices = useStore((s) => s.notices[room.id]);
  const agents = people.filter((p) => p.kind === "agent" && room.members.some((m) => m.userId === p.id));
  const offline = agents.filter((a) => !a.online);
  const busy = agents.filter((a) => thinking?.includes(a.id));
  if (!busy.length && !offline.length && !notices?.length) return null;
  return (
    <div className="agent-status" aria-live="polite">
      {busy.map((a) => (
        <div key={a.id} className="thinking">
          <Avatar person={a} size={18} />
          <span>{a.name} is thinking</span>
          <span className="dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </div>
      ))}
      {offline.map((a) => (
        <div key={a.id} className="offline">
          {a.name} is offline
        </div>
      ))}
      {notices?.map((n) => (
        <div key={n.id} className="note">
          {n.text}
        </div>
      ))}
    </div>
  );
}

function Composer({ room }: { room: Room }) {
  const people = useStore((s) => s.boot?.people ?? EMPTY);
  const title = useStore((s) => roomTitle(s, room));
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pick, setPick] = useState<{ query: string; start: number; index: number } | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  // Files added to this message: uploaded right away, sent with it.
  const [files, setFiles] = useState<{ key: number; name: string; file?: FileView; error?: string }[]>([]);
  const [dragging, setDragging] = useState(false);
  const nextKey = useRef(0);

  useEffect(() => {
    setText("");
    setError(null);
    setPick(null);
    setFiles([]);
    ref.current?.focus();
  }, [room.id]);

  function addFiles(list: FileList | File[]) {
    const picked = [...list].slice(0, 5 - files.length);
    for (const f of picked) {
      const key = nextKey.current++;
      setFiles((xs) => [...xs, { key, name: f.name || "pasted image" }]);
      uploadFile(room.id, f).then(
        (file) => setFiles((xs) => xs.map((x) => (x.key === key ? { ...x, file, name: file.name } : x))),
        (err) => setFiles((xs) => xs.map((x) => (x.key === key ? { ...x, error: err instanceof ApiError ? err.message : "Could not upload." } : x))),
      );
    }
  }
  const uploading = files.some((f) => !f.file && !f.error);
  const ready = files.filter((f) => f.file).map((f) => f.file!.id);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Empty: back to one row (measuring then would wrap the placeholder before the box has a width).
    el.style.height = "";
    if (text) el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [text]);

  const quote = useStore((s) => s.quotes[room.id]);
  useEffect(() => {
    if (!quote) return;
    setText((t) => (t.trim() ? `${t.replace(/\s+$/, "")}\n\n${quote}` : quote));
    quoteInto(room.id, null);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
  }, [quote, room.id]);

  const members = useMemo(() => people.filter((p) => room.members.some((m) => m.userId === p.id)), [people, room.members]);
  const hasAgents = room.kind === "room" && members.some((p) => p.kind === "agent");
  const options = pick
    ? [...(hasAgents && EVERY_AGENT.name.startsWith(pick.query.toLowerCase()) ? [EVERY_AGENT] : []), ...members.filter((p) => p.name.toLowerCase().includes(pick.query.toLowerCase()))].slice(0, 6)
    : [];

  if (!room.isMember) {
    return (
      <div className="composer join">
        <span>You are viewing #{room.name}.</span>
        {room.canJoin && (
          <button className="btn primary small" type="button" onClick={() => void joinRoom(room.id)}>
            Join the room
          </button>
        )}
      </div>
    );
  }

  function onChange(value: string, caret: number) {
    setText(value);
    const before = value.slice(0, caret);
    const m = before.match(/(^|\s)@([\p{L}\p{N} ]{0,30})$/u);
    setPick(m ? { query: m[2]!.trimStart(), start: caret - m[2]!.length - 1, index: 0 } : null);
  }

  function choose(p: Person) {
    if (!pick) return;
    const caret = ref.current?.selectionStart ?? text.length;
    const next = `${text.slice(0, pick.start)}@${p.name} ${text.slice(caret)}`;
    setText(next);
    setPick(null);
    requestAnimationFrame(() => {
      const pos = pick.start + p.name.length + 2;
      ref.current?.setSelectionRange(pos, pos);
      ref.current?.focus();
    });
  }

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if ((!text.trim() && !ready.length) || busy || uploading) return;
    setBusy(true);
    setError(null);
    try {
      await sendMessage(room.id, text, ready);
      setText("");
      setFiles([]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send. Please try again.");
    } finally {
      setBusy(false);
      ref.current?.focus();
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (pick && options.length) {
      if (e.key === "ArrowDown") return (e.preventDefault(), setPick({ ...pick, index: (pick.index + 1) % options.length }));
      if (e.key === "ArrowUp") return (e.preventDefault(), setPick({ ...pick, index: (pick.index - 1 + options.length) % options.length }));
      if (e.key === "Enter" || e.key === "Tab") return (e.preventDefault(), choose(options[pick.index]!));
      if (e.key === "Escape") return setPick(null);
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  }

  return (
    <form
      className={`composer${dragging ? " dragging" : ""}`}
      onSubmit={submit}
      onDragOver={(e) => {
        if (![...e.dataTransfer.types].includes("Files")) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => e.currentTarget.contains(e.relatedTarget as Node) || setDragging(false)}
      onDrop={(e) => {
        if (!e.dataTransfer.files.length) return;
        e.preventDefault();
        setDragging(false);
        addFiles(e.dataTransfer.files);
      }}
    >
      <ErrorNote error={error} />
      {files.length > 0 && (
        <ul className="attachments" aria-label="Files to send">
          {files.map((f) => (
            <li key={f.key} className={f.error ? "failed" : f.file ? "" : "busy"}>
              {f.file?.kind === "image" ? <img src={`/api/files/${f.file.id}`} alt="" /> : <IconDoc />}
              <span>
                {f.name}
                <small>{f.error ?? (f.file ? sizeLabel(f.file.size) : "uploading")}</small>
              </span>
              <button
                type="button"
                aria-label={`Remove ${f.name}`}
                onClick={() => {
                  if (f.file) void removeUpload(f.file.id);
                  setFiles((xs) => xs.filter((x) => x.key !== f.key));
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {pick && options.length > 0 && (
        <div className="mention-pick" role="listbox" aria-label="Mention someone">
          {options.map((p, i) => (
            <button key={p.id} type="button" role="option" aria-selected={i === pick.index} className={i === pick.index ? "on" : ""} onMouseDown={(e) => (e.preventDefault(), choose(p))}>
              {p === EVERY_AGENT ? (
                <>
                  <span className="every-agent" aria-hidden="true">
                    <IconRobot size={13} />
                  </span>
                  agents
                  <span className="agent-badge">every agent, one after the other</span>
                </>
              ) : (
                <>
                  <Avatar person={p} size={20} />
                  {p.name}
                  {p.kind === "agent" && <span className="agent-badge">agent</span>}
                </>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="composer-box">
        <label htmlFor="composer" className="sr">
          Message {title}
        </label>
        <textarea
          id="composer"
          ref={ref}
          rows={1}
          value={text}
          placeholder={room.kind === "dm" ? `Message ${firstName(title)}` : `Message #${room.name}`}
          onChange={(e) => onChange(e.target.value, e.target.selectionStart)}
          onKeyDown={onKeyDown}
          onPaste={(e) => {
            if (!e.clipboardData.files.length) return;
            e.preventDefault();
            addFiles(e.clipboardData.files);
          }}
          maxLength={4000}
        />
        <input ref={picker} type="file" multiple accept={ACCEPT} hidden onChange={(e) => (e.target.files && addFiles(e.target.files), (e.target.value = ""))} />
        <button className="icon-btn attach" type="button" onClick={() => picker.current?.click()} disabled={files.length >= 5} title="Add files: images, PDF, text" aria-label="Add files">
          <IconClip />
        </button>
        <StopClaude room={room} />
        <button className="btn primary small" type="submit" disabled={busy || uploading || (!text.trim() && !ready.length)}>
          Send
        </button>
      </div>
      <small className="hint">Enter sends, Shift and Enter adds a line, @ mentions someone, @agents asks every agent here. Drop or paste images and documents to add them.</small>
    </form>
  );
}

function RoomPanel({ room, onClose }: { room: Room; onClose: () => void }) {
  const boot = useStore((s) => s.boot)!;
  const navigate = useNavigate();
  const byId = new Map(boot.people.map((p) => [p.id, p]));
  const members = room.members
    .map((m) => ({ ...m, person: byId.get(m.userId) }))
    .filter((m) => m.person)
    .sort((a, b) => (a.role === b.role ? a.person!.name.localeCompare(b.person!.name) : a.role === "owner" ? -1 : 1));
  const outside = boot.people.filter((p) => !room.members.some((m) => m.userId === p.id) && p.kind === "human");

  if (room.kind === "dm") return null;
  return (
    <aside className="panel-right" aria-label="Room details">
      <div className="panel-head">
        <h2>About this room</h2>
        <button className="icon-btn" type="button" onClick={onClose} aria-label="Close the room details">
          ×
        </button>
      </div>
      <div className="panel-body">
        <p className="muted-text">{room.purpose || "No purpose yet."}</p>
        <p className="muted-text small">{room.isPrivate ? "Private: only members can see it." : "Public: everyone in the workspace can find it."}</p>

        <h3>Members</h3>
        <ul className="members">
          {members.map((m) => (
            <li key={m.userId}>
              <PersonTrigger person={m.person!} className="member-trigger">
                <Avatar person={m.person!} size={26} showOnline />
                <span className="name">
                  {m.person!.name}
                  {m.person!.kind === "agent" && <IconRobot />}
                </span>
              </PersonTrigger>
              {m.role === "owner" && <span className="chip">owner</span>}
              {m.person!.role === "guest" && <span className="chip">guest</span>}
              {room.canManage && m.userId !== boot.me.id && (m.person!.kind !== "agent" || boot.workspace.role === "admin") && (
                <button className="linkish" type="button" onClick={() => confirm(`Remove ${m.person!.name} from #${room.name}?`) && void removeMember(room.id, m.userId)}>
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>

        <RoomProject room={room} />
        <RoomTasks room={room} />
        <div className="mini-form">
          <h3>Decisions</h3>
          <DecisionList url={`/api/rooms/${room.id}/decisions`} hereRoomId={room.id} empty="None yet. Save one from a message: More, Save as decision." />
        </div>
        {boot.workspace.role === "admin" && <AgentSwitches room={room} agents={boot.people.filter((p) => p.kind === "agent")} />}
        <RoomHost room={room} agents={boot.people.filter((p) => p.kind === "agent")} />
        {room.canManage && <AddPeople room={room} outside={outside} />}
        {room.canManage && <InviteByEmail room={room} isAdmin={boot.workspace.role === "admin"} />}
        {room.canManage && <RoomSettings room={room} colors={boot.colors} />}

        {room.isMember && (
          <button
            className="btn ghost"
            type="button"
            onClick={async () => {
              if (!confirm(`Leave #${room.name}?`)) return;
              await removeMember(room.id, boot.me.id);
              navigate("/");
            }}
          >
            Leave room
          </button>
        )}
      </div>
    </aside>
  );
}

/** Open tasks of this room (done ones on request), and a New task button for members. */
function RoomTasks({ room }: { room: Room }) {
  const { list, error } = useTaskList(`/api/rooms/${room.id}/tasks`);
  const [showDone, setShowDone] = useState(false);
  const [adding, setAdding] = useState(false);
  const open = (list ?? []).filter((t) => t.status !== "done" && t.status !== "cancelled").sort(byUrgency);
  const closed = (list ?? []).filter((t) => t.status === "done").sort(byUrgency);
  return (
    <div className="mini-form">
      <div className="block-head">
        <h3>Tasks</h3>
        {room.isMember && (
          <button className="linkish" type="button" onClick={() => setAdding(true)}>
            New task
          </button>
        )}
      </div>
      <ErrorNote error={error} />
      {list && !open.length && <p className="muted-text small">No open tasks. Create one here or from a message: More, Create task.</p>}
      <ul className="task-list">
        {open.map((t) => (
          <TaskRow key={t.id} task={t} hereRoomId={room.id} />
        ))}
        {showDone && closed.map((t) => <TaskRow key={t.id} task={t} hereRoomId={room.id} />)}
      </ul>
      {closed.length > 0 && (
        <button className="linkish" type="button" onClick={() => setShowDone(!showDone)}>
          {showDone ? "Hide done" : `Show ${closed.length} done`}
        </button>
      )}
      {adding && <NewTaskModal roomId={room.id} onClose={() => setAdding(false)} />}
    </div>
  );
}

/** The project this room works on: shown to everyone, changed by whoever manages the room. */
function RoomProject({ room }: { room: Room }) {
  const projects = useStore((s) => s.boot!.projects);
  const [error, setError] = useState<string | null>(null);
  const current = projects.find((p) => p.id === room.projectId);
  const choices = projects.filter((p) => p.status !== "archived");
  if (!room.canManage && !current) return null;
  return (
    <div className="mini-form">
      <h3>Project</h3>
      {room.canManage ? (
        <select
          value={room.projectId ?? ""}
          aria-label="Project of this room"
          onChange={async (e) => {
            setError(null);
            try {
              await api(`/api/rooms/${room.id}/project`, { projectId: e.target.value || null }, "PUT");
              await loadBootstrap();
            } catch (err) {
              setError(err instanceof ApiError ? err.message : "Could not save.");
            }
          }}
        >
          <option value="">No project</option>
          {choices.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.repo})
            </option>
          ))}
        </select>
      ) : null}
      {current && (
        <small className="muted-text">
          Agents here get the <Link to={`/p/${current.id}`}>{current.name}</Link> card and its repo docs with every question.
        </small>
      )}
      {room.canManage && !choices.length && <small className="muted-text">No projects yet. An admin creates them in the sidebar, Projects.</small>}
      {error && <small className="picture-error">{error}</small>}
    </div>
  );
}

/** The room's host: answers without an @ when the message is for the agents. */
function RoomHost({ room, agents }: { room: Room; agents: Person[] }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const here = agents.filter((a) => room.members.some((m) => m.userId === a.id));
  if (room.kind !== "room") return null;
  if (!here.length)
    return (
      <div className="mini-form">
        <h3>Host</h3>
        <small className="muted-text">No agents in this room yet. Once an admin switches some on under Agents, you can pick a host here and ask them all with @agents.</small>
      </div>
    );
  const host = here.find((a) => a.id === room.hostAgentId) ?? null;
  return (
    <div className="mini-form">
      <h3>Host</h3>
      <small className="muted-text">
        The host answers without an @: every message when you are the only person in the room, otherwise the ones meant for the agents. @agents asks every agent here, one after the other.
      </small>
      {room.canManage ? (
        <select
          value={host?.id ?? ""}
          disabled={busy}
          aria-label="Room host"
          onChange={async (e) => {
            setBusy(true);
            setError(null);
            try {
              await setRoomHost(room.id, e.target.value || null);
            } catch (err) {
              setError(err instanceof ApiError ? err.message : "Could not change the host.");
            } finally {
              setBusy(false);
            }
          }}
        >
          <option value="">No host</option>
          {here.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      ) : (
        <p className="host-line">{host ? host.name : "No host"}</p>
      )}
      {host && host.agent?.kind !== "api" && <small className="muted-text">{host.name} runs on the owner's plan, so it hosts only for the workspace owner.</small>}
      <ErrorNote error={error} />
    </div>
  );
}

function AgentSwitches({ room, agents }: { room: Room; agents: Person[] }) {
  const [busy, setBusy] = useState<string | null>(null);
  if (!agents.length) return null;
  return (
    <div className="mini-form">
      <h3>Agents</h3>
      {agents.map((a) => {
        const on = room.members.some((m) => m.userId === a.id);
        return (
          <div key={a.id} className="switch-row">
            <Avatar person={a} size={24} />
            <span className="switch-text">
              {a.name} in this room
              <small>
                {a.agent?.model ? `${a.agent.model} · ` : ""}
                {a.online ? "on" : a.agent?.kind === "api" ? "switched off" : "offline"} · answers when @mentioned{a.agent && a.agent.kind !== "api" ? " by you" : ""}
                {room.hostAgentId === a.id ? " · host" : ""}
              </small>
            </span>
            <button
              type="button"
              className="toggle"
              role="switch"
              aria-checked={on}
              aria-label={`${a.name} in this room`}
              disabled={busy === a.id}
              onClick={async () => {
                setBusy(a.id);
                try {
                  if (on) await removeMember(room.id, a.id);
                  else await addMembers(room.id, [a.id]);
                } finally {
                  setBusy(null);
                }
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

function AddPeople({ room, outside }: { room: Room; outside: Person[] }) {
  const [userId, setUserId] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (!outside.length) return null;
  return (
    <form
      className="mini-form"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!userId) return;
        try {
          await addMembers(room.id, [userId]);
          setUserId("");
        } catch (err) {
          setError(err instanceof ApiError ? err.message : "Could not add.");
        }
      }}
    >
      <h3>Add people</h3>
      <ErrorNote error={error} />
      <div className="inline">
        <select value={userId} onChange={(e) => setUserId(e.target.value)} aria-label="Person to add">
          <option value="">Choose someone</option>
          {outside.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <button className="btn primary small" type="submit" disabled={!userId}>
          Add
        </button>
      </div>
    </form>
  );
}

function InviteByEmail({ room, isAdmin }: { room: Room; isAdmin: boolean }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "guest">("guest");
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="mini-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        setNote(null);
        try {
          const status = await sendInvite({ email, role: isAdmin ? role : "guest", roomIds: [room.id] });
          setNote(status === "added" ? `${email} is already in the workspace, so they were added to the room.` : `Invite sent to ${email}.`);
          setEmail("");
        } catch (err) {
          setError(err instanceof ApiError ? err.message : "Could not send the invite.");
        }
      }}
    >
      <h3>Invite by email</h3>
      <ErrorNote error={error} />
      {note && <div className="notice ok">{note}</div>}
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" required aria-label="Email to invite" />
      {isAdmin ? (
        <select value={role} onChange={(e) => setRole(e.target.value as "member" | "guest")} aria-label="Role">
          <option value="guest">Guest: sees only the rooms they are invited to</option>
          <option value="member">Member: sees every public room</option>
        </select>
      ) : (
        <small className="muted-text">They join as a Guest and see only the rooms they are invited to.</small>
      )}
      <button className="btn primary small" type="submit" disabled={!email}>
        Send invite
      </button>
    </form>
  );
}

function RoomSettings({ room, colors }: { room: Room; colors: string[] }) {
  const [name, setName] = useState(room.name);
  const [purpose, setPurpose] = useState(room.purpose);
  const [color, setColor] = useState(room.color);
  const [isPrivate, setPrivate] = useState(room.isPrivate);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setName(room.name);
    setPurpose(room.purpose);
    setColor(room.color);
    setPrivate(room.isPrivate);
  }, [room.id, room.name, room.purpose, room.color, room.isPrivate]);
  return (
    <form
      className="mini-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        try {
          await updateRoomSettings(room.id, { name, purpose, color, isPrivate });
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        } catch (err) {
          setError(err instanceof ApiError ? err.message : "Could not save.");
        }
      }}
    >
      <h3>Room settings</h3>
      <ErrorNote error={error} />
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required aria-label="Room name" />
      <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} maxLength={300} rows={2} placeholder="Purpose" aria-label="Purpose" />
      <div className="swatches">
        {colors.map((c) => (
          <button key={c} type="button" className={`swatch${c === color ? " on" : ""}`} style={{ background: c }} onClick={() => setColor(c)} aria-label={`Color ${c}`} aria-pressed={c === color} />
        ))}
      </div>
      <label className="check">
        <input type="checkbox" checked={isPrivate} onChange={(e) => setPrivate(e.target.checked)} />
        <span>Private room</span>
      </label>
      <button className="btn ghost small" type="submit">
        {saved ? "Saved" : "Save changes"}
      </button>
    </form>
  );
}

